import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { 
  Menu,
  MessageCircle, 
  User, 
  RefreshCw,
  X,
  Settings,
  Bell
} from 'lucide-react'
import {
  SidebarContainer,
  Sidebar,
  ToggleButton,
  SidebarContent,
  ActionsContainer,
  ActionItem,
  ActionIcon,
  ActionContent,
  ActionTitle,
  ActionDescription,
  NotificationBadge,
  UpdateBadge,
  ActionSeparator,
  SidebarHeader,
  SidebarTitle,
  SidebarSubtitle,
  CloseButton,
  MobileBackdrop,
  FloatingLabel
} from './styled'

const ExpandableSidebar = ({ 
  onMessagesClick, 
  onProfileClick, 
  onRefreshClick,
  unreadMessages = 0, 
  needsProfileUpdate = false,
  currentUser = null 
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [isMobile, setIsMobile] = useState(false)

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768)
    }
    
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Close sidebar when clicking outside on mobile
  const handleBackdropClick = () => {
    if (isMobile) {
      setIsExpanded(false)
    }
  }

  // Handle ESC key to close
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isExpanded) {
        setIsExpanded(false)
      }
    }

    document.addEventListener('keydown', handleEscKey)
    return () => document.removeEventListener('keydown', handleEscKey)
  }, [isExpanded])

  const handleToggle = () => {
    setIsExpanded(!isExpanded)
  }

  const handleActionClick = (action) => {
    // Close sidebar on mobile after action
    if (isMobile) {
      setIsExpanded(false)
    }
    
    // Execute the action
    switch(action) {
      case 'messages':
        onMessagesClick && onMessagesClick()
        break
      case 'profile':
        onProfileClick && onProfileClick()
        break
      case 'refresh':
        onRefreshClick && onRefreshClick()
        break
      default:
        break
    }
  }

  return (
    <>
      {/* Mobile Backdrop */}
      <MobileBackdrop 
        isVisible={isExpanded && isMobile} 
        onClick={handleBackdropClick} 
      />
      
      <SidebarContainer>
        <Sidebar isExpanded={isExpanded}>
          {/* Toggle Button */}
          <ToggleButton onClick={handleToggle}>
            {isExpanded ? (
              <X size={20} color="white" />
            ) : (
              <Menu size={20} color="white" />
            )}
          </ToggleButton>

          {/* Close Button for Mobile */}
          <CloseButton isExpanded={isExpanded} onClick={() => setIsExpanded(false)}>
            <X size={16} />
          </CloseButton>

          {/* Sidebar Content */}
          <SidebarContent isExpanded={isExpanded}>
            <SidebarHeader>
              <SidebarTitle>Quick Actions</SidebarTitle>
              <SidebarSubtitle>Stay connected and updated</SidebarSubtitle>
            </SidebarHeader>

            <ActionsContainer>
              {/* Messages Action */}
              <ActionItem onClick={() => handleActionClick('messages')}>
                <ActionIcon type="messages">
                  <MessageCircle size={20} color="white" />
                  {unreadMessages > 0 && (
                    <NotificationBadge>
                      {unreadMessages > 99 ? '99+' : unreadMessages}
                    </NotificationBadge>
                  )}
                </ActionIcon>
                <ActionContent>
                  <ActionTitle>Messages</ActionTitle>
                  <ActionDescription>
                    {unreadMessages > 0 
                      ? `${unreadMessages} new message${unreadMessages > 1 ? 's' : ''}`
                      : 'View event announcements'
                    }
                  </ActionDescription>
                </ActionContent>
                {!isExpanded && (
                  <FloatingLabel>
                    Messages {unreadMessages > 0 && `(${unreadMessages})`}
                  </FloatingLabel>
                )}
              </ActionItem>

              {/* Profile Update Action */}
              <ActionItem onClick={() => handleActionClick('profile')}>
                <ActionIcon type="profile">
                  <User size={20} color="white" />
                  {needsProfileUpdate && <UpdateBadge />}
                </ActionIcon>
                <ActionContent>
                  <ActionTitle>Update Profile</ActionTitle>
                  <ActionDescription>
                    {needsProfileUpdate 
                      ? 'Complete your profile setup'
                      : 'Edit your event information'
                    }
                  </ActionDescription>
                </ActionContent>
                {!isExpanded && (
                  <FloatingLabel>
                    Update Profile {needsProfileUpdate && '(Required)'}
                  </FloatingLabel>
                )}
              </ActionItem>

              <ActionSeparator />

              {/* Refresh/Reload Action */}
              <ActionItem onClick={() => handleActionClick('refresh')}>
                <ActionIcon type="refresh">
                  <RefreshCw size={18} color="white" />
                </ActionIcon>
                <ActionContent>
                  <ActionTitle>Refresh Event</ActionTitle>
                  <ActionDescription>
                    Update attendee list and info
                  </ActionDescription>
                </ActionContent>
                {!isExpanded && (
                  <FloatingLabel>Refresh Event</FloatingLabel>
                )}
              </ActionItem>
            </ActionsContainer>
          </SidebarContent>
        </Sidebar>
      </SidebarContainer>
    </>
  )
}

ExpandableSidebar.propTypes = {
  onMessagesClick: PropTypes.func,
  onProfileClick: PropTypes.func,
  onRefreshClick: PropTypes.func,
  unreadMessages: PropTypes.number,
  needsProfileUpdate: PropTypes.bool,
  currentUser: PropTypes.object,
}

ExpandableSidebar.defaultProps = {
  onMessagesClick: () => {},
  onProfileClick: () => {},
  onRefreshClick: () => {},
  unreadMessages: 0,
  needsProfileUpdate: false,
  currentUser: null,
}

export default ExpandableSidebar